import { useState } from 'react';
import { Users, ArrowRightLeft, UserMinus } from 'lucide-react';
import type { SalesTeam, TeamAssignment } from '../../types/salesTeamConfig';

interface TeamMembersListProps {
    assignedByTeam: Map<number, { team: SalesTeam; members: TeamAssignment[] }>;
    teams: SalesTeam[];
    canEdit: boolean;
    onAssign: (salesManagerId: string, salesTeamId: number) => Promise<void>;
    onUnassign: (salesManagerId: string) => Promise<void>;
}

export function TeamMembersList({
    assignedByTeam,
    teams,
    canEdit,
    onAssign,
    onUnassign,
}: TeamMembersListProps) {
    const [changingId, setChangingId] = useState<string | null>(null);
    const [confirmUnassignId, setConfirmUnassignId] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    const handleChangeTeam = async (managerId: string, newTeamId: number) => {
        setBusyId(managerId);
        try {
            await onAssign(managerId, newTeamId);
        } finally {
            setBusyId(null);
            setChangingId(null);
        }
    };

    const handleUnassign = async (managerId: string) => {
        setBusyId(managerId);
        try {
            await onUnassign(managerId);
        } finally {
            setBusyId(null);
            setConfirmUnassignId(null);
        }
    };

    const entries = Array.from(assignedByTeam.entries());

    if (entries.length === 0) {
        return (
            <p className="text-sm text-gray-500 py-4 text-center">
                No teams match the current filters.
            </p>
        );
    }

    return (
        <div className="space-y-4">
            {entries.map(([teamId, { team, members }]) => (
                <div key={teamId} className="rounded-lg border border-gray-200 bg-white">
                    {/* Team header */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
                        <Users className="h-4 w-4 text-gray-500" />
                        <h3 className="text-sm font-semibold text-gray-900">
                            {team.SalesTeamName}
                        </h3>
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            {members.length}
                        </span>
                    </div>

                    {/* Members */}
                    {members.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-400 italic">
                            No members match the current filters.
                        </p>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {members.map(member => (
                                <div
                                    key={member.SalesManagerID}
                                    className="flex items-center gap-3 px-4 py-2.5 text-sm"
                                >
                                    <div className="flex-1 min-w-0">
                                        <span className="font-medium text-gray-900">
                                            {member.SalesManagerName}
                                        </span>
                                        <span className="ml-2 text-gray-500">
                                            {member.SalesManagerCode}
                                        </span>
                                    </div>
                                    <span className="text-xs text-gray-500 uppercase shrink-0">
                                        {member.CompanyCode}
                                    </span>
                                    <span className="text-xs text-gray-400 truncate max-w-[180px] hidden sm:inline">
                                        {member.SalesManagerEmail}
                                    </span>

                                    {canEdit && (
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            {/* Change team */}
                                            {changingId === member.SalesManagerID ? (
                                                <select
                                                    autoFocus
                                                    defaultValue=""
                                                    onChange={e => {
                                                        if (e.target.value) {
                                                            handleChangeTeam(member.SalesManagerID, Number(e.target.value));
                                                        }
                                                    }}
                                                    onBlur={() => setChangingId(null)}
                                                    disabled={busyId === member.SalesManagerID}
                                                    className="rounded border border-gray-300 py-1 px-2 text-xs focus:border-blue-500 focus:outline-none"
                                                >
                                                    <option value="">Move to...</option>
                                                    {teams
                                                        .filter(t => t.SalesTeamID !== teamId)
                                                        .map(t => (
                                                            <option key={t.SalesTeamID} value={t.SalesTeamID}>
                                                                {t.SalesTeamName}
                                                            </option>
                                                        ))}
                                                </select>
                                            ) : (
                                                <button
                                                    onClick={() => setChangingId(member.SalesManagerID)}
                                                    title="Change team"
                                                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600"
                                                >
                                                    <ArrowRightLeft className="h-3.5 w-3.5" />
                                                </button>
                                            )}

                                            {/* Remove */}
                                            {confirmUnassignId === member.SalesManagerID ? (
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleUnassign(member.SalesManagerID)}
                                                        disabled={busyId === member.SalesManagerID}
                                                        className="rounded bg-red-600 px-2 py-0.5 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                                                    >
                                                        Confirm
                                                    </button>
                                                    <button
                                                        onClick={() => setConfirmUnassignId(null)}
                                                        className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-300"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setConfirmUnassignId(member.SalesManagerID)}
                                                    title="Remove from team"
                                                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                                                >
                                                    <UserMinus className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
